import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import CarouselRight from '@rover/icons/svg/carousel-right.svg';
import { Box, BoxProps, ButtonProps, Flex, FlexProps, Text, TextProps } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import useResizeObserver from '@rover/react-lib/src/hooks/useResizeObserver';

import { AccordionContext, AccordionItemColorOption, AccordionItemProps } from './Accordion.common';

const INDENT_AMT = '8x';

//
// Item Heading Text
//
type ItemHeadingTextProps = Pick<TextProps, 'children' | 'size' | 'textColor'>;
const ItemHeadingText: React.FC<ItemHeadingTextProps> = ({ children, size, textColor }) => {
  return (
    <Text
      display="inline-block"
      fontWeight="semibold"
      paddingY="4x"
      size={size}
      textColor={textColor}
    >
      {children}
    </Text>
  );
};

//
// Item Heading Click Target
//
type ClickTargetProps = Pick<ButtonProps, 'children' | 'onPress'> &
  FlexProps & {
    buttonId: string;
  };
const ClickTarget: React.FC<ClickTargetProps> = ({ children, onPress, buttonId, ...flexProps }) => (
  <Flex
    {...flexProps}
    webAs="button"
    webAsProps={{ id: buttonId, type: 'button' }}
    onPress={onPress}
    // ↓ props to override browser styling
    backgroundColor="transparent"
    border="0 none"
    padding="0x"
    // ↓ custom styles

    width="100%"
    textAlign="left"
    flexDirection="row"
    alignItems="center"
    justifyContent="flex-start"
    sx={{ cursor: 'pointer', appearance: 'none' }}
  >
    {children}
  </Flex>
);

//
// Item Heading Icon
//
type ItemHeadingIconProps = Pick<AccordionItemProps, 'isOpen'> & { fillColor: string };
const ItemHeadingIcon: React.FC<ItemHeadingIconProps> = ({ isOpen, fillColor }) => {
  return (
    <Flex flex="0 0 auto" width={INDENT_AMT} justifyContent="center" display="inline-flex">
      <CarouselRight
        width="14px"
        height="14px"
        fill={fillColor}
        style={{ transform: isOpen ? 'rotate(-90deg)' : 'rotate(90deg)' }}
      />
    </Flex>
  );
};

//
// Accordion Item Heading
//
type AccordionItemHeadingProps = BoxProps &
  Pick<AccordionItemProps, 'isOpen' | 'itemHeading'> & {
    headingSize: TextProps['size'];
    handlePress: () => void;
    bodyId: string;
    buttonId: string;
    itemColorVariant: AccordionItemColorOption;
  };
export const AccordionItemHeading: React.FC<AccordionItemHeadingProps> = ({
  isOpen,
  handlePress,
  bodyId,
  buttonId,
  headingSize,
  itemColorVariant,
  itemHeading,
  ...boxProps
}) => {
  const { colors } = useTheme();

  return (
    // TODO: eventually make itemProp configurable here, too
    <Box webAs="dt" webAsProps={{ itemProp: 'name' }} {...boxProps}>
      <ClickTarget
        buttonId={buttonId}
        aria-controls={bodyId}
        aria-expanded={isOpen}
        onPress={handlePress}
      >
        <>
          <ItemHeadingIcon isOpen={isOpen} fillColor={colors.text[itemColorVariant]} />
          <ItemHeadingText size={headingSize} textColor={itemColorVariant}>
            {itemHeading}
          </ItemHeadingText>
        </>
      </ClickTarget>
    </Box>
  );
};

//
// Item Body Content
//
type ItemBodyContentProps = Pick<BoxProps, 'children' | 'color'>;
const ItemBodyContent = React.forwardRef<HTMLElement, ItemBodyContentProps>(
  ({ children, color }, ref) => {
    /* A separate container is required here for applying Y-axis
     * padding. If we put the padding on the parent, the parent's
     * animation wouldn't look right.
     *
     * We also need this container for the secondary schema prop (itemProp)
     */
    return (
      <Box
        ref={ref}
        webProps={{ itemProp: 'text' }} // TODO: eventually make this configurable, too
        paddingBottom="4x"
        paddingLeft={INDENT_AMT}
        color={color}
      >
        {children}
      </Box>
    );
  }
);

//
// Accordion Item Body
//
type AccordionItemBodyProps = BoxProps &
  Pick<AccordionItemProps, 'isOpen' | 'itemBody'> & {
    maxHeight: number;
    bodyContentRef: React.RefObject<HTMLElement | null>;
    itemColorVariant: AccordionItemColorOption;
  };
export const AccordionItemBody: React.FC<AccordionItemBodyProps> = ({
  isOpen,
  maxHeight,
  itemBody,
  bodyContentRef,
  itemColorVariant,
  ...boxProps
}) => {
  const { colors } = useTheme();

  return (
    // this Box does the max-height transition for the animation
    <Box
      webAs="dd"
      height="auto"
      maxHeight={isOpen ? `${maxHeight}px` : '0'}
      borderBottom={`1px solid ${colors.border.primary}`}
      sx={{
        overflowY: 'hidden',
        transition: `max-height 0.25s ease-in-out`,
        '&:focus-within': { maxHeight: `${maxHeight}px` },
      }}
      {...boxProps}
    >
      <ItemBodyContent ref={bodyContentRef} color={colors.text[itemColorVariant]}>
        {itemBody}
      </ItemBodyContent>
    </Box>
  );
};

//
// AccordionItem full component
//
const AccordionItem: React.FC<AccordionItemProps> = ({
  itemHeading,
  itemBody,
  isOpen: $isOpen = false,
  onToggle,
  testID,
}) => {
  const { itemColorVariant, headingSize, itemSchemaProps, bodySchemaProps } =
    useContext(AccordionContext);

  const bodyId = useId();
  const buttonId = useId();
  const itemBodyRef = useRef<HTMLElement>(null);
  const [isOpen, setIsOpen] = useState($isOpen || false);
  const [maxHeight, setMaxHeight] = useState(0);

  // Set isOpen state from prop
  useEffect(() => {
    if ($isOpen !== undefined) {
      setIsOpen($isOpen);
    }
  }, [$isOpen]);

  // Update maxHeight
  const updateMaxHeight = useCallback((): void => {
    if (itemBodyRef.current) {
      const calculatedHeight = itemBodyRef.current.offsetHeight;
      setMaxHeight(calculatedHeight);
    }
  }, [itemBodyRef, setMaxHeight]);

  // Calculate maxHeight before paint to avoid an initial maxHeight that breaks the animation
  useLayoutEffect(() => {
    if (maxHeight === 0) {
      updateMaxHeight();
    }
  }, [updateMaxHeight, maxHeight]);

  // ResizeObserver re-calculates on any content size change to avoid clipped text
  useResizeObserver(itemBodyRef, updateMaxHeight);

  // OnPress Handler
  const handlePress = useCallback((): void => {
    onToggle?.(!isOpen);
    setIsOpen(!isOpen);
  }, [onToggle, setIsOpen, isOpen]);

  //
  // Return
  //
  return (
    <Box webProps={itemSchemaProps} testID={testID || 'accordion-item'}>
      <AccordionItemHeading
        bodyId={bodyId}
        buttonId={buttonId}
        handlePress={handlePress}
        isOpen={isOpen}
        headingSize={headingSize}
        itemHeading={itemHeading}
        itemColorVariant={itemColorVariant}
      />
      <AccordionItemBody
        isOpen={isOpen}
        maxHeight={maxHeight}
        bodyContentRef={itemBodyRef}
        itemBody={itemBody}
        itemColorVariant={itemColorVariant}
        // ↓ box props
        aria-labelledby={buttonId}
        webProps={{ id: bodyId, ...bodySchemaProps }}
      />
    </Box>
  );
};

export default AccordionItem;
